/**
 * WorkbookTabs.jsx
 * Multi-sheet tab strip. Renders one tab per sheet from workbook metadata.
 * Shows dirty-row dot indicator per tab.
 */

import React, { memo } from 'react';
import { Plus } from 'lucide-react';
import { useSheetStore } from '../../../libraries/spreadsheet/store/spreadsheetStores';

const WorkbookTabs = memo(({
  sheets,          // [{ sheetId, sheetName }]
  activeSheetId,
  onSelectSheet,   // (sheetId) => void
  onAddSheet,      // () => void  (optional)
  canAddSheet,     // bool
}) => {
  const dirtyRowsMap = useSheetStore((s) => s.dirtyRowsMap);

  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-end',
      background: '#f1f5f9',
      borderBottom: '1px solid #e2e8f0',
      padding: '0 12px',
      overflowX: 'auto',
      flexShrink: 0,
      gap: 2,
    }}>
      {sheets.map((sheet) => {
        const isActive   = sheet.sheetId === activeSheetId;
        const dirtyCount = (dirtyRowsMap[sheet.sheetId] || new Set()).size;

        return (
          <button
            key={sheet.sheetId}
            onClick={() => onSelectSheet(sheet.sheetId)}
            title={sheet.sheetName}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 16px 6px',
              fontSize: 11,
              fontWeight: isActive ? 700 : 500,
              fontFamily: "'DM Sans', sans-serif",
              border: 'none',
              borderBottom: isActive ? '2px solid #1a56db' : '2px solid transparent',
              borderRadius: '4px 4px 0 0',
              background: isActive ? '#ffffff' : 'transparent',
              color: isActive ? '#1a56db' : '#6b7280',
              cursor: 'pointer',
              transition: 'all 0.12s ease',
              whiteSpace: 'nowrap',
              outline: 'none',
            }}
          >
            {sheet.sheetName}
            {dirtyCount > 0 && (
              <span
                title={`${dirtyCount} unsaved changes`}
                style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: '#f59e0b', flexShrink: 0, display: 'inline-block',
                }}
              />
            )}
          </button>
        );
      })}

      {canAddSheet && (
        <button
          onClick={onAddSheet}
          title="Add sheet"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 28, height: 28, marginLeft: 4, marginBottom: 2,
            border: '1px dashed #d1d5db', borderRadius: 4,
            background: 'transparent', cursor: 'pointer', color: '#9ca3af',
          }}
        >
          <Plus size={12} />
        </button>
      )}
    </div>
  );
});

export default WorkbookTabs;
